import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Users, Store, ShoppingCart, TrendingUp,
  Star, Package, CheckCircle, RotateCcw,
  ArrowLeft, ArrowUpRight, DollarSign,
} from 'lucide-react'
import { vendorsService } from '@/services/vendorsService'
import { ordersService } from '@/services/ordersService'
import { analyticsService } from '@/services/analyticsService'
import StatsCard from '@/components/shared/StatsCard'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import StatusBadge from '@/components/shared/StatusBadge'
import { formatCurrency, formatRelativeTime, getLocalizedField } from '@/utils/formatters'
import { ROUTES } from '@/constants/routes'

export default function DashboardPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const lang = i18n.language

  // ── Queries ───────────────────────────────────────────────────────────────
  // One overview call replaces multiple individual count queries
  const { data: overview } = useQuery({
    queryKey: ['analytics-overview'],
    queryFn: analyticsService.getOverview,
    retry: 1,
  })

  const { data: pendingVendorsData } = useQuery({
    queryKey: ['vendors-pending'],
    queryFn: () => vendorsService.getAll({ status: 'pending', limit: 5 }),
    retry: 1,
  })

  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ['orders-recent'],
    queryFn: () => ordersService.getAll({ limit: 8, page: 1 }),
    retry: 1,
  })

  // Derived totals — from overview (single API call)
  const ov             = overview || {}
  const totalUsers     = ov.totalUsers     ?? '—'
  const totalVendors   = ov.totalVendors   ?? '—'
  const totalOrders    = ov.totalOrders    ?? '—'
  const pendingVendors = pendingVendorsData?.meta?.total ?? 0
  const pendingReviews = ov.pendingReviews ?? 0

  const recentOrders = ordersData?.data || []

  // ── Quick action cards ───────────────────────────────────────────────────
  const quickActions = [
    {
      label: 'مراجعة طلبات البائعين',
      sublabel: pendingVendors > 0 ? `${pendingVendors} طلب بانتظار المراجعة` : 'لا توجد طلبات معلقة',
      icon: Store,
      color: '#1B6D24',
      tint: 'rgba(27,109,36,0.08)',
      route: ROUTES.VENDORS,
      badge: pendingVendors,
    },
    {
      label: 'مراجعة التقييمات',
      sublabel: pendingReviews > 0 ? `${pendingReviews} تقييم قيد الانتظار` : 'لا توجد تقييمات معلقة',
      icon: Star,
      color: '#FF6D00',
      tint: 'rgba(255,109,0,0.08)',
      route: ROUTES.REVIEWS,
      badge: pendingReviews,
    },
    {
      label: 'موافقة المنتجات',
      sublabel: ov.pendingProducts > 0 ? `${ov.pendingProducts} منتج قيد المراجعة` : 'لا توجد منتجات معلقة',
      icon: Package,
      color: '#000666',
      tint: 'rgba(0,6,102,0.06)',
      route: ROUTES.PRODUCTS,
      badge: ov.pendingProducts,
    },
    {
      label: 'المرتجعات المعلقة',
      sublabel: ov.pendingReturns > 0 ? `${ov.pendingReturns} طلب إرجاع معلق` : 'لا توجد مرتجعات معلقة',
      icon: RotateCcw,
      color: ov.pendingReturns > 0 ? '#BA1A1A' : '#767683',
      tint: ov.pendingReturns > 0 ? 'rgba(186,26,26,0.06)' : 'rgba(118,118,131,0.06)',
      route: ROUTES.RETURNS,
      badge: ov.pendingReturns,
    },
  ]

  return (
    <div className="space-y-6">

      {/* ── KPI Stats ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatsCard
          icon={DollarSign}
          label="إجمالي الإيرادات (GMV)"
          value={ov.totalRevenue != null ? formatCurrency(ov.totalRevenue) : '—'}
          color="#1B6D24"
          sub={ov.totalPlatformIncome != null ? `${formatCurrency(ov.totalPlatformIncome)} دخل المنصة` : null}
        />
        <StatsCard
          icon={ShoppingCart}
          label={t('dashboard.totalOrders')}
          value={typeof totalOrders === 'number' ? totalOrders.toLocaleString('ar-EG') : totalOrders}
          color="#000666"
          sub={ov.pendingOrders > 0 ? `${ov.pendingOrders} طلب معلق` : null}
        />
        <StatsCard
          icon={Users}
          label={t('dashboard.totalUsers')}
          value={typeof totalUsers === 'number' ? totalUsers.toLocaleString('ar-EG') : totalUsers}
          color="#000666"
          sub={ov.activeCarts > 0 ? `${ov.activeCarts} عربة نشطة` : null}
        />
        <StatsCard
          icon={Store}
          label={t('dashboard.totalVendors')}
          value={typeof totalVendors === 'number' ? totalVendors.toLocaleString('ar-EG') : totalVendors}
          color="#1B6D24"
          sub={pendingVendors > 0 ? `${pendingVendors} قيد المراجعة` : null}
        />
      </div>

      {/* ── Main content grid ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

        {/* Recent Orders — takes 2 cols */}
        <div className="xl:col-span-2 hg-card p-5">
          <div className="flex items-center justify-between mb-5">
            <h2
              className="font-black text-navy text-lg"
              style={{ fontFamily: 'Cairo, sans-serif' }}
            >
              {t('dashboard.recentOrders')}
            </h2>
            <button
              onClick={() => navigate(ROUTES.ORDERS)}
              className="flex items-center gap-1.5 text-sm font-bold text-orange hover:text-orange/80 transition-colors"
              style={{ fontFamily: 'Cairo, sans-serif', color: '#FF6D00' }}
            >
              {t('dashboard.viewAll')}
              <ArrowLeft size={14} />
            </button>
          </div>

          {ordersLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : recentOrders.length === 0 ? (
            <div className="text-center py-12 text-ghost" style={{ fontFamily: 'Tajawal, sans-serif' }}>
              لا توجد طلبات حتى الآن
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '2px solid #F3F3F3' }}>
                    {['رقم الطلب', 'العميل', 'الإجمالي', 'الحالة', 'التاريخ'].map((h) => (
                      <th
                        key={h}
                        className="text-right pb-3 text-xs font-bold text-ghost uppercase tracking-wide"
                        style={{ fontFamily: 'Tajawal, sans-serif', paddingLeft: 12, paddingRight: 4 }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-container">
                  {recentOrders.map((order) => (
                    <tr
                      key={order.id}
                      className="hover:bg-surface-canvas transition-colors cursor-pointer"
                      onClick={() => navigate(ROUTES.ORDERS)}
                    >
                      <td className="py-3 px-1">
                        <span
                          className="font-bold text-navy text-sm"
                          style={{ fontFamily: 'Cairo, sans-serif' }}
                        >
                          {order.orderNumber}
                        </span>
                      </td>
                      <td className="py-3 px-1">
                        <span className="text-sm text-ink" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                          {order.user?.name || '—'}
                        </span>
                      </td>
                      <td className="py-3 px-1">
                        <span
                          className="font-bold text-sm"
                          style={{ color: '#FF6D00', fontFamily: 'Cairo, sans-serif' }}
                        >
                          {formatCurrency(order.total)}
                        </span>
                      </td>
                      <td className="py-3 px-1">
                        <StatusBadge status={order.status} type="order" lang={lang} />
                      </td>
                      <td className="py-3 px-1">
                        <span className="text-xs text-ghost" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                          {formatRelativeTime(order.createdAt, lang === 'ar' ? 'ar-EG' : 'en')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Vendor Applications — 1 col */}
        <div className="hg-card p-5">
          <div className="flex items-center justify-between mb-5">
            <h2
              className="font-black text-navy text-lg"
              style={{ fontFamily: 'Cairo, sans-serif' }}
            >
              {t('dashboard.vendorRequests')}
            </h2>
            <button
              onClick={() => navigate(ROUTES.VENDORS + '?status=pending')}
              className="text-sm font-bold"
              style={{ color: '#FF6D00', fontFamily: 'Cairo, sans-serif' }}
            >
              {t('dashboard.viewAll')}
            </button>
          </div>

          {(pendingVendorsData?.data || []).length === 0 ? (
            <div className="text-center py-8">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3"
                style={{ background: 'rgba(27,109,36,0.08)', color: '#1B6D24' }}
              >
                <CheckCircle size={24} />
              </div>
              <p className="text-sm text-ghost" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                لا توجد طلبات معلقة
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {(pendingVendorsData?.data || []).map((vendor) => (
                <div
                  key={vendor.id}
                  className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all hover:bg-surface-canvas"
                  onClick={() => navigate(ROUTES.VENDORS)}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm text-white"
                    style={{ background: '#1B6D24' }}
                  >
                    {getLocalizedField(vendor.storeName, lang)?.charAt(0) || 'V'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className="font-bold text-sm text-ink truncate"
                      style={{ fontFamily: 'Cairo, sans-serif' }}
                    >
                      {getLocalizedField(vendor.storeName, lang)}
                    </div>
                    <div
                      className="text-xs text-ghost truncate"
                      style={{ fontFamily: 'Tajawal, sans-serif' }}
                    >
                      {vendor.user?.name} · {vendor.city}
                    </div>
                  </div>
                  <span className="badge-pending flex-shrink-0">جديد</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Quick Actions ──────────────────────────────────────────────────── */}
      <div className="hg-card p-5">
        <h2
          className="font-black text-navy text-lg mb-4"
          style={{ fontFamily: 'Cairo, sans-serif' }}
        >
          {t('dashboard.quickActions')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map(({ label, sublabel, icon: Icon, color, tint, route, badge }) => (
            <button
              key={label}
              onClick={() => navigate(route)}
              className="flex items-center gap-4 p-4 rounded-xl text-right transition-all hover:-translate-y-1 hover:shadow-card"
              style={{ background: tint, border: `1px solid ${color}20` }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 relative"
                style={{ background: `${color}15`, color }}
              >
                <Icon size={22} />
                {badge > 0 && (
                  <span
                    className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center"
                    style={{ background: '#FF6D00' }}
                  >
                    {badge}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className="font-bold text-sm truncate"
                  style={{ color, fontFamily: 'Cairo, sans-serif' }}
                >
                  {label}
                </div>
                <div
                  className="text-xs mt-0.5 truncate"
                  style={{ color: `${color}80`, fontFamily: 'Tajawal, sans-serif' }}
                >
                  {sublabel}
                </div>
              </div>
              <ArrowUpRight size={16} style={{ color, opacity: 0.5, flexShrink: 0 }} />
            </button>
          ))}
        </div>
      </div>

    </div>
  )
}
